// 004: shared pieces for the profile screens (cards, section headings, avatar, offline note).
import type { ReactNode } from "react";
import { ScrollView, StyleSheet, View } from "react-native";

import { makeStyles } from "@/theme/makeStyles";
import { InlineMessage } from "@/ui/InlineMessage";
import { Text } from "@/ui/Text";

import { PROFILE_COPY } from "./copy";

export const useProfileStyles = makeStyles((t) =>
  StyleSheet.create({
    screen: { flex: 1, backgroundColor: t.color.background },
    scroll: {
      flexGrow: 1,
      alignItems: "center",
      paddingHorizontal: t.space.lg,
      paddingVertical: t.space.x2l,
    },
    column: { width: "100%", maxWidth: 1080, gap: t.space.lg },
    narrow: { width: "100%", maxWidth: 560, gap: t.space.lg },
    card: {
      gap: t.space.md,
      padding: t.space.xl,
      borderRadius: t.radius.card,
      borderWidth: t.borderWidth.card,
      borderColor: t.color.border,
      backgroundColor: t.color.surface,
    },
    sectionHead: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: t.space.md,
      flexWrap: "wrap",
    },
    headText: { flexShrink: 1, gap: t.space.xxs },
    row: {
      flexDirection: "row",
      alignItems: "center",
      gap: t.space.md,
      paddingVertical: t.space.sm,
      borderTopWidth: t.borderWidth.card,
      borderTopColor: t.color.border,
    },
    rowText: { flex: 1, gap: t.space.xxs },
    actions: { flexDirection: "row", gap: t.space.xs, flexWrap: "wrap" },
    avatar: {
      width: t.size.iconButton,
      height: t.size.iconButton,
      borderRadius: t.radius.pill,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: t.color.primarySoft,
    },
    avatarText: { color: t.color.primaryText },
    badge: {
      paddingHorizontal: t.space.sm,
      paddingVertical: t.space.xxs,
      borderRadius: t.radius.pill,
      backgroundColor: t.color.successSoft,
    },
    badgeText: { color: t.color.success },
    split: { flexDirection: "row", gap: t.space.lg, alignItems: "flex-start" },
    side: { width: 320, gap: t.space.lg },
    main: { flex: 1, gap: t.space.lg },
    buttons: { flexDirection: "row", gap: t.space.sm, flexWrap: "wrap" },
  }),
);

export function ProfileFrame({
  children,
  testID,
  narrow = false,
}: {
  children: ReactNode;
  testID: string;
  narrow?: boolean;
}) {
  const styles = useProfileStyles();
  return (
    <View style={styles.screen} testID={testID}>
      <ScrollView
        contentContainerStyle={styles.scroll}
        keyboardShouldPersistTaps="handled"
        role="main"
      >
        <View style={narrow ? styles.narrow : styles.column}>{children}</View>
      </ScrollView>
    </View>
  );
}

export function Card({
  children,
  testID,
}: {
  children: ReactNode;
  testID?: string;
}) {
  const styles = useProfileStyles();
  return (
    <View style={styles.card} testID={testID}>
      {children}
    </View>
  );
}

export function SectionHead({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  const styles = useProfileStyles();
  return (
    <View style={styles.sectionHead}>
      <View style={styles.headText}>
        <Text
          variant="heading"
          size="lg"
          accessibilityRole="header"
          aria-level={2}
        >
          {title}
        </Text>
        {subtitle ? <Text tone="muted">{subtitle}</Text> : null}
      </View>
      {action}
    </View>
  );
}

export function Avatar({ text }: { text: string }) {
  const styles = useProfileStyles();
  return (
    <View style={styles.avatar} aria-hidden>
      <Text variant="heading" size="md" style={styles.avatarText}>
        {text}
      </Text>
    </View>
  );
}

export function OfflineNote({ online }: { online: boolean }) {
  if (online) return null;
  return (
    <InlineMessage tone="info">{PROFILE_COPY.offlineChange}</InlineMessage>
  );
}
